import { createContext, useContext, useMemo } from 'react';
import useQueuedAccountModalAction from '~/hooks/common/accountModal/useQueuedAccountModalAction';

type DeferredActionContextValue = {
  executeOrQueueAccountModalAction: (action: () => void) => void;
  isWaitingForAccountModalReady: boolean;
};

export const defaultContext: DeferredActionContextValue = {
  executeOrQueueAccountModalAction() {},
  isWaitingForAccountModalReady: false
};

const DeferredActionContext =
  createContext<DeferredActionContextValue>(defaultContext);

/**
 * Provider that manages deferred action queueing when dependencies aren't ready yet.
 * Prevents dead clicks when users interact with UI before asynchronous modules load.
 *
 * Currently used to handle logged-out users clicking
 * "Get Started" before the header/account modal infrastructure is ready.
 *
 * Can be extended to handle other early-interaction scenarios where user actions
 * need to wait for asynchronous module loading (modals, MFEs, external scripts, etc).
 */
export function DeferredActionProvider(props: React.PropsWithChildren) {
  const {
    executeOrQueue: executeOrQueueAccountModalAction,
    isWaitingForReady: isWaitingForAccountModalReady
  } = useQueuedAccountModalAction();

  const value = useMemo<DeferredActionContextValue>(
    () => ({
      executeOrQueueAccountModalAction,
      isWaitingForAccountModalReady
    }),
    [executeOrQueueAccountModalAction, isWaitingForAccountModalReady]
  );

  return (
    <DeferredActionContext.Provider value={value}>
      {props.children}
    </DeferredActionContext.Provider>
  );
}

export const useDeferredAction = (): DeferredActionContextValue =>
  useContext(DeferredActionContext);
